import {useEvent} from 'Event/EventProvider'
import React from 'react'
import PropertySelector from 'organization/Event/Services/Apps/HubspotV2/Config/PropertySelector'
import PropertyValueField from 'organization/Event/Services/Apps/HubspotV2/Config/PropertyValueField'
import {
  Property,
  useHubspotV2Properties,
} from 'organization/Event/Services/Apps/HubspotV2/Config/HubspotV2PropertiesProvider'
import {Label} from 'lib/ui/typography'
import styled from 'styled-components'
import {TargetProperty} from 'organization/Event/Services/Apps/HubspotV2/Config'
import {HubspotV2Tag} from 'Event/hubspot-v2'

export type TagInputProps = {
  onChange: (tag: HubspotV2Tag | null) => void
  value?: HubspotV2Tag | null
  preselectedProperty?: TargetProperty | null
  targetTypes?: Property['type'][]
  disabled?: boolean
  label?: string
  inputVariant?: 'filled' | 'standard' | 'outlined'
  inputLabel?: string
  errorText?: string
}

export default function TagInput(props: TagInputProps) {
  const {
    onChange,
    disabled,
    value,
    targetTypes,
    label,
    preselectedProperty,
    errorText,
  } = props
  const {event} = useEvent()

  const {findProperty: findByName} = useHubspotV2Properties()

  const getProperty = () => {
    if (preselectedProperty !== undefined) {
      return preselectedProperty
    }

    if (value?.property_name) {
      return findByName(value?.property_name)
    }

    return null
  }

  const property = getProperty()

  if (!event.has_hubspot_v2 || disabled) {
    return null
  }

  const setProperty = (selected: TargetProperty | null) => {
    onChange({
      property_value: value?.property_value ?? '',
      property_label: selected?.label ?? null,
      property_name: selected?.name ?? null,
      property_type: selected?.type ?? null,
    })
  }

  const setValue = (propertyValue: string) => {
    if (!value) {
      return
    }

    onChange({
      ...value,
      property_label: preselectedProperty?.label ?? value.property_label,
      property_name: preselectedProperty?.name ?? value.property_name,
      property_type: preselectedProperty?.type ?? value.property_type,
      property_value: propertyValue,
    })
  }

  return (
    <div>
      {label !== '' && <StyledLabel>{label ?? 'HubspotV2 tag'}</StyledLabel>}
      <Box>
        <PropertySelector
          targetTypes={targetTypes}
          onChange={setProperty}
          value={property}
          label={label}
          locked={!!preselectedProperty}
          errorText={errorText}
        />
        <ValueField
          propertyName={property?.name}
          onChange={setValue}
          propertyValue={value?.property_value}
          propertyType={property?.type}
        />
      </Box>
    </div>
  )
}

function ValueField(props: {
  propertyName?: string | null
  propertyValue?: string | null
  propertyType?: Property['type'] | null
  onChange: (value: string) => void
}) {
  const {propertyName, onChange, propertyType, propertyValue} = props

  if (!propertyName || !propertyType) {
    return null
  }

  return (
    <PropertyValueField
      onChange={onChange}
      propertyName={propertyName}
      propertyType={propertyType}
      value={propertyValue || ''}
    />
  )
}

const StyledLabel = styled(Label)`
  margin-bottom: ${(props) => props.theme.spacing[4]} !important;
`

const Box = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[1]};

  > div {
    flex: 1;
  }
`
